import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Paper, List, ListItem,
  ListItemText, ListItemAvatar, Avatar, Chip, Divider, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  IconButton, Menu, MenuItem, Collapse, LinearProgress, Select, FormControl, InputLabel
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Person as PersonIcon, Add as AddIcon, Receipt as ReceiptIcon,
  Group as GroupIcon, CheckCircle as CheckCircleIcon, MoreVert as MoreVertIcon,
  Edit as EditIcon, Delete as DeleteIcon, Lock as LockIcon,
  History as HistoryIcon, ExpandMore as ExpandMoreIcon,
  AccountBalanceWallet as WalletIcon, CameraAlt as CameraIcon,
  TrendingUp as TrendingUpIcon, ArrowForward as ArrowForwardIcon,
  Star as StarIcon
} from '@mui/icons-material';
import SplitSmartAPI from '../../services/splitSmartAPI';
import { useAuth } from '../../contexts/AuthContext';
import {
  AccountCircle as AccountCircleIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import SEOHead from '../SEO/SEOHead';

const EXPENSE_CATEGORIES = [
  'general', 'food', 'travel', 'accommodation', 'entertainment', 'shopping', 'utilities'
];

const INITIAL_EXPENSE_FORM = { description: '', amount: '', category: 'general', sharedBy: [], paidBy: null };

const CATEGORY_EMOJIS = {
  general: '📦', food: '🍕', travel: '✈️', accommodation: '🏨',
  entertainment: '🎬', shopping: '🛍️', utilities: '⚡'
};

/**
 * JoinBillPage — Public guest-access page for shared SplitSmart bills.
 *
 * Accessed via /join/:shareCode. No authentication required.
 * Allows guests to view bill details, join by name, and add / edit / delete expenses.
 */
const JoinBillPage = () => {
  const { shareCode } = useParams();
  const theme = useTheme();

  // ── Auth context (for "Link to Account" feature) ──────────────────────
  const { user } = useAuth();
  const isAuthenticated = !!user;

  // ── State ──────────────────────────────────────────────────────────────
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestId, setGuestId] = useState('');
  const [hasJoined, setHasJoined] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState(INITIAL_EXPENSE_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);       // null = add, number = edit
  const [deleteConfirm, setDeleteConfirm] = useState(null);     // expense index or null
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});    // index → boolean
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [linking, setLinking] = useState(false);
  const [linked, setLinked] = useState(false);

  // ── Guest identity (persist across refreshes via localStorage) ─────────
  useEffect(() => {
    let storedId = localStorage.getItem(`guest_${shareCode}`);
    if (!storedId) {
      storedId = 'guest_' + Math.random().toString(36).substr(2, 9);
    }
    setGuestId(storedId);

    const storedName = localStorage.getItem(`guest_name_${shareCode}`);
    if (storedName) {
      setGuestName(storedName);
      setHasJoined(true);
    }
  }, [shareCode]);

  // ── Load bill data ─────────────────────────────────────────────────────
  useEffect(() => {
    const loadBill = async () => {
      setLoading(true);
      try {
        const result = await SplitSmartAPI.getBillByShareCode(shareCode);
        if (result.success) {
          setBill(result.data.bill);
        } else {
          setError(result.error || 'Bill not found');
        }
      } catch {
        setError('Unable to load bill. Please try again later.');
      }
      setLoading(false);
    };
    loadBill();
  }, [shareCode]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleJoin = async () => {
    const trimmed = guestName.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await SplitSmartAPI.joinBillAsGuest(shareCode, trimmed, guestId);
      if (result.success) {
        localStorage.setItem(`guest_${shareCode}`, guestId);
        localStorage.setItem(`guest_name_${shareCode}`, trimmed);
        setHasJoined(true);
        setBill(result.data.bill);
      } else {
        setError(result.error || 'Failed to join bill');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const handleExpenseSubmit = async () => {
    if (!expenseForm.description || !expenseForm.amount) return;

    setSubmitting(true);
    setError(null);
    try {
      let result;
      if (editingIndex !== null) {
        // ── Update existing expense ──
        result = await SplitSmartAPI.updateGuestExpense(shareCode, editingIndex, {
          guestId,
          description: expenseForm.description,
          amount: parseFloat(expenseForm.amount),
          category: expenseForm.category || 'general',
          sharedBy: expenseForm.sharedBy
        });
      } else {
        // ── Add new expense ──
        const currentGuestIndex = participants.findIndex(p => p.isCurrentGuest);
        const paidByIndex = expenseForm.paidBy !== null ? parseInt(expenseForm.paidBy) : (currentGuestIndex >= 0 ? currentGuestIndex : 0);
        const paidByName = participants[paidByIndex]?.name || guestName;
        result = await SplitSmartAPI.addGuestExpense(shareCode, {
          guestId,
          description: expenseForm.description,
          amount: parseFloat(expenseForm.amount),
          paidByIndex,
          paidByName,
          sharedByIndices: expenseForm.sharedBy,
          category: expenseForm.category || 'general'
        });
      }
      if (result.success) {
        setBill(result.data.bill);
        closeExpenseDialog();
      } else {
        setError(result.error || 'Failed to save expense');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const handleDeleteExpense = async () => {
    if (deleteConfirm === null) return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await SplitSmartAPI.deleteGuestExpense(shareCode, deleteConfirm, guestId);
      if (result.success) {
        setBill(result.data.bill);
      } else {
        setError(result.error || 'Failed to delete expense');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setDeleteConfirm(null);
    setSubmitting(false);
  };

  const handleExpenseFieldChange = (field) => (e) =>
    setExpenseForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleCategorySelect = (cat) => () =>
    setExpenseForm((prev) => ({ ...prev, category: cat }));

  const openExpenseDialog = (index = null) => {
    if (index !== null) {
      const exp = expenses[index];
      setExpenseForm({
        description: exp.description || '',
        amount: exp.amount?.toString() || '',
        category: exp.category || 'general',
        sharedBy: exp.sharedBy?.length > 0 ? [...exp.sharedBy] : bill?.participants?.map((_, i) => i) || []
      });
      setEditingIndex(index);
    } else {
      setExpenseForm({ ...INITIAL_EXPENSE_FORM, sharedBy: bill?.participants?.map((_, i) => i) || [] });
      setEditingIndex(null);
    }
    setShowExpenseDialog(true);
  };

  const closeExpenseDialog = () => {
    setShowExpenseDialog(false);
    setEditingIndex(null);
    setExpenseForm(INITIAL_EXPENSE_FORM);
  };

  const openMenu = (e, index) => { setMenuAnchor(e.currentTarget); setMenuIndex(index); };
  const closeMenu = () => { setMenuAnchor(null); setMenuIndex(null); };
  const toggleHistory = (index) => setExpandedHistory((p) => ({ ...p, [index]: !p[index] }));

  const handleNameEdit = () => { setNameInput(guestName); setEditingName(true); };
  const handleNameSave = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === guestName) { setEditingName(false); return; }
    try {
      const result = await SplitSmartAPI.updateGuestName(shareCode, guestId, trimmed);
      if (result.success) {
        setGuestName(trimmed);
        localStorage.setItem(`guest_name_${shareCode}`, trimmed);
        setBill(result.data.bill);
      } else {
        setError(result.error || 'Failed to update name');
      }
    } catch { setError('Failed to update name'); }
    setEditingName(false);
  };

  const handleLinkToAccount = async () => {
    setLinking(true);
    try {
      const result = await SplitSmartAPI.linkBillToAccount(shareCode);
      if (result.success) {
        setLinked(true);
        setBill(result.data.bill);
      } else {
        setError(result.error || 'Failed to link bill');
      }
    } catch {
      setError('Failed to link bill to your account');
    }
    setLinking(false);
  };

  // ── Derived values ─────────────────────────────────────────────────────
  const currency = bill?.currency || 'USD';
  const totalAmount = (bill?.totalAmount || 0).toFixed(2);
  const participantCount = bill?.participants?.length || 0;
  const expenses = bill?.expenses || [];
  const editLocked = !!bill?.editLocked;

  // Merged + deduplicated participant list — guests already in participants[]
  const guestNameSet = useMemo(() => {
    const s = new Set();
    (bill?.guestParticipants || []).forEach((g) => { if (g.name) s.add(g.name.toLowerCase()); });
    return s;
  }, [bill?.guestParticipants]);

  const participants = useMemo(() => (bill?.participants || []).map((p) => ({
    ...p,
    isCurrentGuest: hasJoined && p.name?.toLowerCase() === guestName?.toLowerCase(),
    isGuest: guestNameSet.has(p.name?.toLowerCase())
  })), [bill?.participants, guestNameSet, hasJoined, guestName]);

  // ── Settlement summary (display-only, derived from expense data) ──────
  const settlements = useMemo(() => {
    if (bill?.whoOwesWhom && bill.whoOwesWhom.length > 0) return bill.whoOwesWhom;
    const method = bill?.splitMethod || 'equal';
    if (method !== 'equal') return [];
    if (!bill?.participants || !bill?.expenses || bill.expenses.length === 0) return [];
    const total = bill.expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const n = bill.participants.length;
    if (n === 0) return [];
    const fairShare = total / n;
    const balances = {};
    bill.participants.forEach((p) => { if (p.name) balances[p.name] = 0; });
    bill.expenses.forEach((e) => {
      const payer = e.paidByName || e.addedBy;
      if (payer && balances[payer] !== undefined) balances[payer] += (e.amount || 0);
    });
    const debtors = [];
    const creditors = [];
    Object.entries(balances).forEach(([name, paid]) => {
      const net = paid - fairShare;
      if (net < -0.01) debtors.push({ name, amount: -net });
      else if (net > 0.01) creditors.push({ name, amount: net });
    });
    debtors.sort((a, b) => b.amount - a.amount);
    creditors.sort((a, b) => b.amount - a.amount);
    const result = [];
    let di = 0, ci = 0;
    while (di < debtors.length && ci < creditors.length) {
      const amt = Math.min(debtors[di].amount, creditors[ci].amount);
      if (amt > 0.01) result.push({ from: debtors[di].name, to: creditors[ci].name, amount: amt.toFixed(2) });
      debtors[di].amount -= amt;
      creditors[ci].amount -= amt;
      if (debtors[di].amount < 0.01) di++;
      if (creditors[ci].amount < 0.01) ci++;
    }
    return result;
  }, [bill?.whoOwesWhom, bill?.splitMethod, bill?.participants, bill?.expenses]);

  // ── Display helpers ────────────────────────────────────────────────────
  const creatorName = bill?.creator?.name || 'a friend';
  const expenseProgress = participantCount > 0
    ? Math.min((expenses.length / Math.max(participantCount, 1)) * 100, 100) : 0;

  // ── Loading state ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center', minHeight: '100vh' }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
        }}>
          <WalletIcon sx={{ fontSize: 36, color: 'primary.main' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', mb: 0.5 }}>
          SoldiKeeper
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          SplitSmart
        </Typography>
        <CircularProgress size={28} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">Loading your shared bill…</Typography>
      </Container>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────
  if (error && !bill) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, minHeight: '100vh' }}>
        <Paper elevation={0} sx={{
          p: 5, textAlign: 'center', borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.error.main, 0.08),
          }}>
            <ReceiptIcon sx={{ fontSize: 36, color: 'error.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Bill Not Found</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This share link may have expired or been disabled.
          </Typography>
          <Button variant="contained" href="/"
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, minHeight: 48, px: 4 }}>
            Go to SoldiKeeper
          </Button>
        </Paper>
      </Container>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────
  return (
    <Container maxWidth="sm" sx={{ pt: 2, pb: 4, minHeight: '100vh' }}>
      <SEOHead title="Join Shared Expense" noIndex={true} canonical="/join" description="Join a shared expense bill on SoldiKeeper." />

      {/* ═══════════════════ Header / Branding ═══════════════════ */}
      <Paper elevation={0} sx={{
        textAlign: 'center', py: 4, px: 3, mb: 3, borderRadius: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative background circles */}
        <Box sx={{
          position: 'absolute', top: -40, right: -30,
          width: 140, height: 140, borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }} />
        <Box sx={{
          position: 'absolute', bottom: -30, left: -20,
          width: 100, height: 100, borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }} />
        <Box sx={{
          position: 'absolute', top: 20, left: 10,
          width: 50, height: 50, borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          }}>
            <WalletIcon sx={{ fontSize: 30, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{
            fontWeight: 800, color: 'primary.main', letterSpacing: '-0.02em',
          }}>
            SoldiKeeper
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 2 }}>
            SplitSmart — Split expenses effortlessly
          </Typography>
          <Chip
            icon={<PersonIcon sx={{ fontSize: 16 }} />}
            label={`Invited by ${creatorName}`}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              fontWeight: 600, fontSize: '0.8rem', height: 32,
            }}
          />
        </Box>
      </Paper>

      {/* ═══════════════════ Bill Summary — Hero Card ═══════════════════ */}
      <Paper elevation={0} sx={{
        p: 3, borderRadius: 3, mb: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}>
        <Typography variant="h4" sx={{
          fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5, lineHeight: 1.2,
        }}>
          {bill.title}
        </Typography>
        {bill.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {bill.description}
          </Typography>
        )}

        {/* Big total amount */}
        <Typography variant="h3" sx={{
          fontWeight: 900, color: 'primary.main',
          letterSpacing: '-0.03em', my: 2, lineHeight: 1,
        }}>
          {currency} {totalAmount}
        </Typography>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
          <Chip
            icon={<GroupIcon sx={{ fontSize: 16 }} />}
            label={`${participantCount} people`} size="small"
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), fontWeight: 600, height: 28 }}
          />
          <Chip
            label={
              (bill.splitMethod || 'equal').charAt(0).toUpperCase() +
              (bill.splitMethod || 'equal').slice(1) + ' split'
            }
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.info?.main || theme.palette.primary.main, 0.08),
              fontWeight: 600, height: 28,
            }}
          />
          {bill.category && (
            <Chip
              label={`${CATEGORY_EMOJIS[bill.category] || '📦'} ${bill.category}`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                fontWeight: 600, height: 28, textTransform: 'capitalize',
              }}
            />
          )}
        </Box>

        {/* Expense progress */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
            {expenses.length} expense{expenses.length !== 1 ? 's' : ''} added
          </Typography>
          <LinearProgress
            variant="determinate"
            value={expenseProgress}
            sx={{
              height: 4, borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              '& .MuiLinearProgress-bar': { borderRadius: 2 },
            }}
          />
        </Box>

        {bill.createdAt && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            Created {new Date(bill.createdAt).toLocaleDateString(undefined, {
              month: 'short', day: 'numeric', year: 'numeric',
            })}
          </Typography>
        )}
      </Paper>

      {/* ═══════════════════ Link to Account (Authenticated Users) ═══════════════════ */}
      {isAuthenticated && !hasJoined && !linked && (
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 3, mb: 2,
          border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
          bgcolor: alpha(theme.palette.success.main, 0.03),
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', top: -15, right: -15,
            width: 80, height: 80, borderRadius: '50%',
            bgcolor: alpha(theme.palette.success.main, 0.05),
          }} />
          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Avatar sx={{
              width: 52, height: 52, mx: 'auto', mb: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main',
            }}>
              <AccountCircleIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              You have a SoldiKeeper account!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Save this bill to your dashboard for full tracking, insights & settlement tools.
            </Typography>
            <Button fullWidth variant="contained" color="success" onClick={handleLinkToAccount}
              disabled={linking}
              startIcon={linking ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
              sx={{
                borderRadius: 3, textTransform: 'none', fontWeight: 700,
                minHeight: 52, fontSize: '1.05rem', mb: 1.5,
                boxShadow: `0 4px 14px ${alpha(theme.palette.success.main, 0.3)}`,
              }}>
              {linking ? 'Linking…' : 'Link to My Account'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Or continue as guest below
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ═══════════════════ Linked Success Banner ═══════════════════ */}
      {isAuthenticated && linked && (
        <Paper elevation={0} sx={{
          p: 2.5, borderRadius: 3, mb: 2,
          bgcolor: alpha(theme.palette.success.main, 0.06),
          border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Avatar sx={{
              width: 40, height: 40,
              bgcolor: alpha(theme.palette.success.main, 0.15), color: 'success.main',
            }}>
              <CheckCircleIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                ✅ Bill saved to your SoldiKeeper account
              </Typography>
              <Typography variant="caption" color="text.secondary">
                View it anytime from your dashboard
              </Typography>
            </Box>
          </Box>
          <Button fullWidth variant="outlined" color="success" href="/splitwise"
            sx={{
              borderRadius: 3, textTransform: 'none', fontWeight: 700,
              minHeight: 44,
            }}>
            View in Dashboard
          </Button>
        </Paper>
      )}

      {/* ═══════════════════ Join Card ═══════════════════ */}
      {!hasJoined && (
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 3, mb: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}>
          {/* Illustration-like icon composition */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'inline-flex', gap: 1.5, mb: 2.5 }}>
              <Avatar sx={{
                width: 52, height: 52,
                bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main',
              }}>
                <PersonIcon />
              </Avatar>
              <Avatar sx={{
                width: 52, height: 52,
                bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main',
              }}>
                <ReceiptIcon />
              </Avatar>
              <Avatar sx={{
                width: 52, height: 52,
                bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main',
              }}>
                <GroupIcon />
              </Avatar>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              You've been invited to split expenses
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Join this bill to add your expenses and settle up
            </Typography>
          </Box>

          <TextField
            fullWidth label="Your name" value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Enter your name to join"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1.1rem' },
              '& .MuiInputLabel-root': { fontSize: '1rem' },
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
          />
          <Button fullWidth variant="contained" onClick={handleJoin}
            disabled={!guestName.trim() || submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : <PersonIcon />}
            sx={{
              borderRadius: 3, textTransform: 'none', fontWeight: 700,
              minHeight: 52, fontSize: '1.05rem',
            }}>
            {submitting ? 'Joining…' : 'Join Bill'}
          </Button>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mt: 2, textAlign: 'center', fontWeight: 500 }}>
            No account needed • Takes 2 seconds
          </Typography>
        </Paper>
      )}

      {/* ═══════════════════ Joined Confirmation + Add Expense ═══════════════════ */}
      {hasJoined && (
        <Paper elevation={0} sx={{
          p: 2.5, borderRadius: 3, mb: 2,
          bgcolor: alpha(theme.palette.success.main, 0.06),
          border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{
                width: 40, height: 40,
                bgcolor: alpha(theme.palette.success.main, 0.15), color: 'success.main',
              }}>
                <CheckCircleIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                  Joined as {guestName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  You can now add expenses
                </Typography>
              </Box>
            </Box>
            {!editLocked && (
              <Button variant="contained" size="medium" startIcon={<AddIcon />}
                onClick={() => openExpenseDialog()}
                sx={{
                  borderRadius: 3, textTransform: 'none', fontWeight: 700,
                  minHeight: 48, px: 2.5,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
                }}>
                Add Expense
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* ═══════════════════ Link After Guest Join ═══════════════════ */}
      {hasJoined && isAuthenticated && !linked && (
        <Paper elevation={0} sx={{
          p: 2, borderRadius: 3, mb: 2,
          border: `1px solid ${alpha(theme.palette.info?.main || theme.palette.primary.main, 0.12)}`,
          bgcolor: alpha(theme.palette.info?.main || theme.palette.primary.main, 0.03),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              width: 36, height: 36,
              bgcolor: alpha(theme.palette.info?.main || theme.palette.primary.main, 0.12),
              color: theme.palette.info?.main || theme.palette.primary.main,
            }}>
              <LinkIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Link this bill to your SoldiKeeper account
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Track it in your dashboard with full features
              </Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={handleLinkToAccount}
              disabled={linking}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 70 }}>
              {linking ? <CircularProgress size={16} /> : 'Link'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Inline error */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}

      {/* ═══════════════════ Expenses List ═══════════════════ */}
      <Paper elevation={0} sx={{
        borderRadius: 3, overflow: 'hidden', mb: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}>
        <Box sx={{
          px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        }}>
          <ReceiptIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            Expenses ({expenses.length})
          </Typography>
          {editLocked && (
            <Chip
              icon={<LockIcon sx={{ fontSize: 14 }} />}
              label="Locked" size="small"
              sx={{ height: 24, fontSize: '0.7rem', bgcolor: alpha(theme.palette.warning.main, 0.08) }}
            />
          )}
        </Box>

        {expenses.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', gap: 1.5, mb: 2.5 }}>
              {['📦', '🍕', '✈️'].map((emoji, i) => (
                <Box key={i} sx={{
                  width: 56, height: 56, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(
                    [theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main][i],
                    0.07,
                  ),
                  fontSize: 26,
                }}>
                  {emoji}
                </Box>
              ))}
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              No expenses yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasJoined && !editLocked
                ? 'Be the first to add an expense!'
                : 'Expenses will appear here once added'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {expenses.map((expense, index) => {
              const payer = expense.paidByName || expense.addedBy || 'Unknown';
              const historyLen = expense.editHistory?.length || 0;
              const isExpanded = !!expandedHistory[index];
              const isCurrentGuestExpense = hasJoined && payer.toLowerCase() === guestName.toLowerCase();
              const emoji = CATEGORY_EMOJIS[expense.category] || CATEGORY_EMOJIS.general;

              return (
                <React.Fragment key={expense._id || index}>
                  {index > 0 && <Divider sx={{ mx: 2 }} />}
                  <ListItem
                    sx={{
                      px: 3, py: 2,
                      ...(isCurrentGuestExpense && {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      }),
                    }}
                    secondaryAction={hasJoined && !editLocked ? (
                      <IconButton edge="end" onClick={(e) => openMenu(e, index)}
                        sx={{ minWidth: 48, minHeight: 48 }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    ) : undefined}
                  >
                    <ListItemAvatar>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        fontSize: 22,
                      }}>
                        {emoji}
                      </Box>
                    </ListItemAvatar>
                    <ListItemText
                      primary={expense.description}
                      secondary={`Paid by ${payer}`}
                      primaryTypographyProps={{ fontWeight: 700, fontSize: '0.95rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                    <Typography variant="subtitle1"
                      sx={{ fontWeight: 800, mr: hasJoined && !editLocked ? 4 : 0 }}>
                      {currency} {expense.amount?.toFixed(2)}
                    </Typography>
                  </ListItem>

                  {/* Edit History */}
                  {historyLen > 0 && (
                    <Box sx={{ px: 3, pb: 1 }}>
                      <Box onClick={() => toggleHistory(index)}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          cursor: 'pointer', userSelect: 'none' }}>
                        <HistoryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          Edited {historyLen} {historyLen === 1 ? 'time' : 'times'}
                        </Typography>
                        <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary',
                          transform: isExpanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s ease' }} />
                      </Box>
                      <Collapse in={isExpanded}>
                        <Box sx={{ mt: 1, pl: 1,
                          borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                          {expense.editHistory.map((entry, hi) => (
                            <Box key={hi} sx={{ pl: 1, mb: 0.75 }}>
                              <Typography variant="caption"
                                sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                                <strong>{entry.editedBy || 'Someone'}</strong>{' changed '}
                                <em>{entry.field}</em>{' from '}&ldquo;{entry.oldValue}&rdquo;
                                {' to '}&ldquo;{entry.newValue}&rdquo;
                              </Typography>
                              {entry.timestamp && (
                                <Typography variant="caption"
                                  sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                  {new Date(entry.timestamp).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Expense row context menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 160 } } }}>
        <MenuItem onClick={() => { openExpenseDialog(menuIndex); closeMenu(); }}
          sx={{ gap: 1, minHeight: 48 }}>
          <EditIcon fontSize="small" /> Edit
        </MenuItem>
        <MenuItem onClick={() => { setDeleteConfirm(menuIndex); closeMenu(); }}
          sx={{ gap: 1, minHeight: 48, color: 'error.main' }}>
          <DeleteIcon fontSize="small" /> Delete
        </MenuItem>
      </Menu>

      {/* ═══════════════════ Participants ═══════════════════ */}
      <Paper elevation={0} sx={{
        borderRadius: 3, overflow: 'hidden', mb: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}>
        <Box sx={{
          px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        }}>
          <GroupIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Participants ({participantCount})
          </Typography>
        </Box>
        <List disablePadding>
          {participants.map((p, i) => {
            const label = p.isCreator ? 'Creator' : p.isCurrentGuest ? '' : p.isGuest ? 'Guest' : '';
            return (
              <React.Fragment key={p._id || `p-${i}`}>
                {i > 0 && <Divider sx={{ mx: 2 }} />}
                <ListItem sx={{
                  px: 3, py: 1.5,
                  ...(p.isCurrentGuest && { bgcolor: alpha(theme.palette.success.main, 0.03) }),
                }}>
                  <ListItemAvatar>
                    <Avatar sx={{
                      width: 38, height: 38, fontSize: '0.85rem', fontWeight: 700,
                      bgcolor: p.isCurrentGuest
                        ? alpha(theme.palette.success.main, 0.15)
                        : p.isCreator
                          ? alpha(theme.palette.primary.main, 0.12)
                          : alpha(theme.palette.primary.main, 0.07),
                      color: p.isCurrentGuest
                        ? 'success.main'
                        : p.isCreator ? 'primary.main' : 'text.secondary',
                    }}>
                      {p.name?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {p.isCurrentGuest && editingName ? (
                          <TextField size="small" value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            onBlur={handleNameSave}
                            onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
                            autoFocus sx={{ width: 140 }}
                            inputProps={{ style: { fontSize: '0.9rem', padding: '4px 8px' } }} />
                        ) : (
                          <span>{p.name}</span>
                        )}
                        {p.isCurrentGuest && !editingName && (
                          <>
                            <Chip label="You" size="small" sx={{
                              height: 22, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: alpha(theme.palette.success.main, 0.12),
                              color: 'success.main',
                            }} />
                            <IconButton size="small" onClick={handleNameEdit}
                              sx={{ ml: 0.25, p: 0.5, minWidth: 28, minHeight: 28 }}>
                              <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            </IconButton>
                          </>
                        )}
                        {p.isCreator && !p.isCurrentGuest && (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: 12 }} />}
                            label="Creator" size="small"
                            sx={{
                              height: 22, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={label && !p.isCreator && !p.isCurrentGuest ? label : undefined}
                    primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600, component: 'div' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      {/* ═══════════════════ Settlement Summary ═══════════════════ */}
      {settlements.length > 0 && (
        <Paper elevation={0} sx={{
          borderRadius: 3, overflow: 'hidden', mb: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}>
          <Box sx={{
            px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          }}>
            <WalletIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Who Owes Whom
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            {settlements.map((s, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                py: 1.5, px: 2, mb: i < settlements.length - 1 ? 1 : 0,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}>
                <Avatar sx={{
                  width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700,
                  bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main',
                }}>
                  {s.from?.[0]?.toUpperCase() || '?'}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                  {s.from}
                </Typography>
                <ArrowForwardIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                <Avatar sx={{
                  width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700,
                  bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main',
                }}>
                  {s.to?.[0]?.toUpperCase() || '?'}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                  {s.to}
                </Typography>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 800, ml: 'auto', color: 'primary.main', flexShrink: 0,
                }}>
                  {currency} {s.amount}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* ═══════════════════ SoldiKeeper Promo Footer ═══════════════════ */}
      <Paper elevation={0} sx={{
        borderRadius: 3, overflow: 'hidden', mt: 3, mb: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        position: 'relative',
      }}>
        {/* Decorative background circle */}
        <Box sx={{
          position: 'absolute', top: -20, right: -20,
          width: 100, height: 100, borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }} />
        <Box sx={{ p: 3, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: '50%', mx: 'auto', mb: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          }}>
            <WalletIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Love splitting expenses?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            SoldiKeeper gives you the full picture of your money
          </Typography>

          {/* Feature highlights */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, textAlign: 'left' }}>
            {[
              { icon: <WalletIcon sx={{ fontSize: 20 }} />, text: 'Track all your expenses in one place', color: theme.palette.primary.main },
              { icon: <TrendingUpIcon sx={{ fontSize: 20 }} />, text: 'Smart budgets & financial insights', color: theme.palette.success.main },
              { icon: <CameraIcon sx={{ fontSize: 20 }} />, text: 'Scan receipts with AI', color: theme.palette.warning.main },
              { icon: <GroupIcon sx={{ fontSize: 20 }} />, text: 'Unlimited group splits with SplitSmart', color: theme.palette.info?.main || theme.palette.primary.main },
            ].map((feature, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{
                  width: 36, height: 36,
                  bgcolor: alpha(feature.color, 0.1), color: feature.color,
                }}>
                  {feature.icon}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* CTA button */}
          {isAuthenticated && linked ? (
            <Button fullWidth variant="contained" href="/splitwise"
              sx={{
                borderRadius: 3, textTransform: 'none', fontWeight: 700,
                minHeight: 52, fontSize: '1.05rem', mb: 2,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}>
              Open Your Dashboard
            </Button>
          ) : (
            <Button fullWidth variant="contained" href="/register"
              sx={{
                borderRadius: 3, textTransform: 'none', fontWeight: 700,
                minHeight: 52, fontSize: '1.05rem', mb: 2,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}>
              Create Your Free Account
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Join 10,000+ users managing money smarter
          </Typography>
        </Box>
      </Paper>

      {/* ═══════════════════ Brand Footer ═══════════════════ */}
      <Box sx={{ textAlign: 'center', py: 3, pb: 4 }}>
        <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>
          © {new Date().getFullYear()} SoldiKeeper — Your money, simplified
        </Typography>
      </Box>

      {/* ═══════════════════ Add / Edit Expense Dialog ═══════════════════ */}
      <Dialog open={showExpenseDialog} onClose={closeExpenseDialog} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1, pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingIndex !== null
              ? <EditIcon sx={{ color: 'primary.main' }} />
              : <AddIcon sx={{ color: 'primary.main' }} />}
            {editingIndex !== null ? 'Edit Expense' : 'Add Expense'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="What was it for?" value={expenseForm.description}
            onChange={handleExpenseFieldChange('description')}
            placeholder="e.g., Dinner, Uber, Groceries"
            sx={{
              mt: 1, mb: 2.5,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }} />
          <TextField fullWidth label="Amount" type="number" value={expenseForm.amount}
            onChange={handleExpenseFieldChange('amount')}
            sx={{
              mb: 2.5,
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1.25rem', fontWeight: 700 },
            }}
            InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }} />
          {hasJoined && participants.length > 1 && (
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <InputLabel>Who paid?</InputLabel>
              <Select
                value={expenseForm.paidBy !== null ? expenseForm.paidBy : participants.findIndex(p => p.isCurrentGuest)}
                label="Who paid?"
                onChange={(e) => setExpenseForm(prev => ({ ...prev, paidBy: e.target.value }))}
                sx={{ borderRadius: 2 }}
              >
                {participants.map((p, i) => (
                  <MenuItem key={i} value={i}>
                    {p.name + (p.isCurrentGuest ? ' (You)' : '')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography variant="caption" color="text.secondary"
            sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
            Category
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {EXPENSE_CATEGORIES.map((cat) => (
              <Chip key={cat}
                label={`${CATEGORY_EMOJIS[cat] || '📦'} ${cat}`}
                size="small" onClick={handleCategorySelect(cat)}
                sx={{
                  borderRadius: 2, textTransform: 'capitalize', px: 0.5,
                  fontWeight: expenseForm.category === cat ? 700 : 400,
                  bgcolor: expenseForm.category === cat
                    ? 'primary.main'
                    : alpha(theme.palette.action.hover, 0.08),
                  color: expenseForm.category === cat ? 'white' : 'text.primary',
                  minHeight: 36,
                  '&:hover': {
                    bgcolor: expenseForm.category === cat
                      ? 'primary.dark'
                      : alpha(theme.palette.action.hover, 0.15),
                  },
                }}
              />
            ))}
          </Box>
          {/* Shared By */}
          {hasJoined && participants.length > 1 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Split between ({expenseForm.sharedBy?.length || 0} of {participants.length})
                </Typography>
                <Button size="small" onClick={() => {
                  const allIndices = participants.map((_, i) => i);
                  const allSelected = expenseForm.sharedBy?.length === allIndices.length;
                  setExpenseForm(prev => ({ ...prev, sharedBy: allSelected ? [participants.findIndex(p => p.isCurrentGuest)] : allIndices }));
                }} sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 36 }}>
                  {expenseForm.sharedBy?.length === participants.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {participants.map((p, i) => {
                  const isSelected = expenseForm.sharedBy?.includes(i);
                  return (
                    <Chip
                      key={i}
                      label={p.name + (p.isCurrentGuest ? ' (You)' : '')}
                      size="small"
                      onClick={() => {
                        setExpenseForm(prev => {
                          const current = prev.sharedBy || [];
                          const updated = isSelected ? current.filter(idx => idx !== i) : [...current, i];
                          return { ...prev, sharedBy: updated.length > 0 ? updated : [i] };
                        });
                      }}
                      sx={{
                        borderRadius: 2,
                        fontWeight: isSelected ? 700 : 400,
                        bgcolor: isSelected ? 'primary.main' : 'action.hover',
                        color: isSelected ? 'white' : 'text.primary',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={closeExpenseDialog}
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48, borderRadius: 3, px: 3 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleExpenseSubmit}
            disabled={!expenseForm.description || !expenseForm.amount || submitting}
            startIcon={submitting ? <CircularProgress size={18} />
              : editingIndex !== null ? <EditIcon /> : <AddIcon />}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, minHeight: 48, flex: 1 }}>
            {submitting ? 'Saving…' : editingIndex !== null ? 'Save Changes' : 'Add Expense'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════ Delete Confirmation Dialog ═══════════════════ */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}
        PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon sx={{ color: 'error.main' }} />
            Delete Expense
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this expense? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm(null)}
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48, borderRadius: 3 }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteExpense} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : <DeleteIcon />}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, minHeight: 48 }}>
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JoinBillPage;
